import { useModuleLogo } from '@/hooks/useModuleLogo'
import { cn, isResolvedUrl } from '@/lib/utils'
import { getClientColor } from '@/lib/utils/client-colors'
import {
  BuildingsIcon,
  CalendarIcon,
  CheckSquareIcon,
  CurrencyDollarIcon,
  FileTextIcon,
  FolderOpenIcon,
  ReceiptIcon,
  UserCheckIcon,
  UserIcon,
  UsersIcon,
} from '@phosphor-icons/react'

import { ModuleTypeEnum } from '@scrambl/shared'

import { Avatar, AvatarFallback, AvatarImage } from './avatar'

interface ModuleEntity {
  id?: string
  name?: string
  code?: string // For projects
  logoUrl?: string // S3 key or signed URL - can be passed here or via logoUrl prop
}

interface ModuleAvatarProps {
  entity: ModuleEntity | null
  moduleType: ModuleTypeEnum
  logoUrl?: string | null // Optional signed URL or S3 key - if not provided, will use entity.logoUrl
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  className?: string
  /** Tonal initials for quieter overview and detail surfaces. */
  tone?: 'solid' | 'subtle'
  showFallbackIcon?: boolean
  onClick?: () => void
  // Preloaded signed logo URL from bulk loading - if provided, skips individual API call
  preloadedLogoUrl?: string | null
  // Whether bulk logo loading is enabled - prevents individual API calls while waiting for bulk data
  skipLogoFetch?: boolean
}

const sizeClasses = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
  '2xl': 'h-32 w-32 text-xl',
}

const iconSizeClasses = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
  xl: 'h-6 w-6',
  '2xl': 'h-12 w-12',
}

// Helper function to get smaller text size for longer codes
const getTextSizeForLongText = (
  size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl',
): string => {
  const textSizeMap = {
    xs: 'text-[0.6rem]', // Even smaller for xs avatars
    sm: 'text-[0.65rem]', // Smaller than default for sm
    md: 'text-xs', // Standard small for md
    lg: 'text-sm', // Slightly smaller for lg
    xl: 'text-base', // Regular size for xl (was xl before)
    '2xl': 'text-lg', // Still large but not huge for 2xl
  }
  return textSizeMap[size] || 'text-xs'
}

// Map module types to their appropriate icons
const moduleTypeIcons = {
  [ModuleTypeEnum.ENTITY]: BuildingsIcon,
  [ModuleTypeEnum.PROJECT]: FolderOpenIcon,
  [ModuleTypeEnum.TASK]: CheckSquareIcon,
  [ModuleTypeEnum.CONTACT]: UserCheckIcon,
  [ModuleTypeEnum.DEAL]: CurrencyDollarIcon,
  [ModuleTypeEnum.EVENT]: CalendarIcon,
  [ModuleTypeEnum.CONTRACT]: FileTextIcon,
  [ModuleTypeEnum.INVOICE]: ReceiptIcon,
  [ModuleTypeEnum.MEMBER]: UserIcon,
  [ModuleTypeEnum.TIME_ENTRY]: CalendarIcon,
  [ModuleTypeEnum.TEAM]: UsersIcon,
  [ModuleTypeEnum.DOCUMENT]: FileTextIcon,
}

/**
 * ModuleAvatar - A universal component for displaying module entity avatars/logos
 *
 * Handles logos for any module type (entities, projects, tasks, etc.) with proper
 * fallbacks to initials or module-specific icons. Reuses the base Avatar component
 * for consistent styling and automatic color generation.
 */
export function ModuleAvatar({
  entity,
  moduleType,
  logoUrl,
  size = 'md',
  className,
  tone = 'solid',
  showFallbackIcon = true,
  onClick,
  preloadedLogoUrl,
  skipLogoFetch = false,
}: ModuleAvatarProps) {
  // Determine if we should use the hook or preloaded URL
  const hasPreloadedLogo = preloadedLogoUrl !== undefined

  // Use the hook to get signed URL if needed (skip if preloaded or skipLogoFetch is true)
  const logoUrlToResolve = logoUrl || entity?.logoUrl
  const { signedLogoUrl } = useModuleLogo({
    moduleId: entity?.id,
    moduleType,
    // Pass null to prevent the hook from fetching if we have preloaded data or should skip
    logoUrl: hasPreloadedLogo || skipLogoFetch ? null : logoUrlToResolve,
  })

  // Priority for final URL:
  // 1. Preloaded signed URL (from bulk loading)
  // 2. Already resolved URL passed as prop (signed S3 URL, CDN URL, blob:, data:)
  // 3. Signed URL from hook
  let finalLogoUrl: string | null = null

  if (hasPreloadedLogo) {
    // Use preloaded signed URL from bulk loading
    finalLogoUrl = preloadedLogoUrl
  } else if (isResolvedUrl(logoUrlToResolve)) {
    // URL is already signed/usable (S3 signed URL, CDN URL, blob:, or data:)
    finalLogoUrl = logoUrlToResolve
  } else if (!skipLogoFetch) {
    // Use hook result (individual API call)
    finalLogoUrl = signedLogoUrl
  }
  // Get entity initials or project code
  const getFallbackText = (
    entity: ModuleEntity,
    moduleType: ModuleTypeEnum,
  ): string => {
    // For projects, prefer code over initials
    if (moduleType === ModuleTypeEnum.PROJECT && entity.code) {
      return entity.code.toUpperCase()
    }

    // For other module types or projects without code, use initials
    const name = entity.name
    if (!name) return ''

    const words = name.trim().split(' ')
    if (words.length === 1) {
      // For single word, take first two letters
      return name.substring(0, 2).toUpperCase()
    }
    // For multiple words, take first letter of each word (max 2)
    return words
      .slice(0, 2)
      .map(word => word[0])
      .join('')
      .toUpperCase()
  }

  // Get the appropriate icon for the module type
  const IconComponent = moduleTypeIcons[moduleType] || BuildingsIcon

  // Determine shape based on module type
  // Use ! modifier to override the base Avatar's rounded-full
  const isRounded = [ModuleTypeEnum.MEMBER, ModuleTypeEnum.CONTACT].includes(
    moduleType,
  )
  const shapeClass = isRounded ? '' : '!rounded-lg'

  // If no entity provided, show icon fallback
  if (!entity || !entity.name) {
    return (
      <Avatar
        className={cn(
          sizeClasses[size],
          shapeClass,
          onClick && 'cursor-pointer transition-opacity hover:opacity-80',
          className,
        )}
        onClick={onClick}
      >
        <AvatarFallback
          className={cn('bg-muted/20 text-muted-foreground', shapeClass)}
        >
          {showFallbackIcon ? (
            <IconComponent className={iconSizeClasses[size]} />
          ) : (
            '?'
          )}
        </AvatarFallback>
      </Avatar>
    )
  }

  const fallbackText = getFallbackText(entity, moduleType)

  return (
    <Avatar
      className={cn(
        sizeClasses[size],
        shapeClass,
        onClick && 'cursor-pointer transition-opacity hover:opacity-80',
        className,
      )}
      onClick={onClick}
    >
      {finalLogoUrl && (
        <AvatarImage
          src={finalLogoUrl}
          alt={entity.name}
          className={shapeClass}
        />
      )}
      <AvatarFallback
        className={cn(
          'font-semibold',
          shapeClass,
          // Make text smaller for longer codes (more than 2 characters)
          fallbackText.length > 2 && getTextSizeForLongText(size),
        )}
        identifier={entity.id || entity.name}
        style={
          tone === 'subtle'
            ? {
                backgroundColor: getClientColor(entity.id || entity.name, {
                  alpha: 0.12,
                }),
                color: 'hsl(var(--foreground))',
              }
            : undefined
        }
      >
        {fallbackText}
      </AvatarFallback>
    </Avatar>
  )
}

// Convenience components for specific module types
export function EntityAvatar(props: Omit<ModuleAvatarProps, 'moduleType'>) {
  return <ModuleAvatar {...props} moduleType={ModuleTypeEnum.ENTITY} />
}

export function ProjectAvatar(props: Omit<ModuleAvatarProps, 'moduleType'>) {
  return <ModuleAvatar {...props} moduleType={ModuleTypeEnum.PROJECT} />
}

export function TaskAvatar(props: Omit<ModuleAvatarProps, 'moduleType'>) {
  return <ModuleAvatar {...props} moduleType={ModuleTypeEnum.TASK} />
}

export function DealAvatar(props: Omit<ModuleAvatarProps, 'moduleType'>) {
  return <ModuleAvatar {...props} moduleType={ModuleTypeEnum.DEAL} />
}
